import { computed, inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch, type ComputedRef, type InjectionKey, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import wizardService, { type WizardState } from '@/services/v2/DataReadinessWizardService';
import clientReadinessService, {
  type PolicyDocumentStatusRow,
  type DocumentType,
  type RuleViolatingPolicy,
  type RuleViolatingProgram,
} from '@/services/v2/ClientReadinessService';
import epicService, { type EpicLastSync } from '@/services/v2/EpicService';
import { accountService } from '@/services/api';
import type { Program, ProgramSummary } from '@/types/Programs';
import type { AccountContact, ProgramContact } from '@/types/AccountContact';
import { AccountContactRow } from '@/types/AccountContact';
import type { EpicPolicy } from '@/types/tower/Domain';
import {
  countDemographicsIssues,
  countDocumentIssues,
  countEpicIssues,
  countPolicyDetailIssues,
} from '@/utilities/dataReadinessIssues';

export type FooterHintColor = 'red' | 'amber' | 'green' | 'muted';

export interface FooterHint {
  text: string;
  color: FooterHintColor;
}

export interface DemographicsInfo {
  domainName: string | null;
  noDomain: boolean;
}

export interface DataReadinessWizardContext {
  accountId: Ref<number>;
  currentStep: Ref<number>;
  setStep: (n: number) => void;
  // Furthest step index the user has reached (persisted in metaData); lets the
  // stepper keep check/warning states on steps ahead of the current one.
  maxStepReached: Ref<number>;

  showCurrentOnly: Ref<boolean>;
  showNonCAC: Ref<boolean>;

  binderRows: Ref<PolicyDocumentStatusRow[]>;
  policyRows: Ref<PolicyDocumentStatusRow[]>;
  ruleViolatingPolicies: Ref<RuleViolatingPolicy[]>;
  lastEpicSync: Ref<EpicLastSync | null>;
  demographicsInfo: Ref<DemographicsInfo | null>;
  programs: Ref<Program[]>;
  ruleViolatingPrograms: Ref<RuleViolatingProgram[]>;
  programSummaries: Ref<ProgramSummary[]>;
  policiesWithoutProgram: Ref<EpicPolicy[]>;

  loadingBinders: Ref<boolean>;
  loadingPolicies: Ref<boolean>;
  loadingRules: Ref<boolean>;
  loadingLastSync: Ref<boolean>;
  loadingDemographics: Ref<boolean>;
  loadingPrograms: Ref<boolean>;
  loadingProgramSummaries: Ref<boolean>;
  loadingPoliciesWithoutProgram: Ref<boolean>;

  // Outstanding issue count per WIZARD_STEPS entry, computed from the shared
  // data above. null = that step's data source hasn't loaded yet (unknown).
  stepIssueCounts: ComputedRef<(number | null)[]>;

  metaData: Ref<Record<string, unknown>>;
  isLoadingState: Ref<boolean>;
  loadWizardState: () => Promise<void>;
  mergeMetaData: (patch: Record<string, unknown>) => void;
  footerHint: Ref<FooterHint | null>;
  setFooterHint: (hint: FooterHint | null) => void;

  // Whether the wizard footer's Next button is allowed to advance. A step can
  // set this false (e.g. an invalid field) to block forward navigation.
  canAdvance: Ref<boolean>;
  setCanAdvance: (v: boolean) => void;
  // A step registers a guard that persists its data before the wizard navigates
  // (Next/Back/stepper). registerNavGuard returns an unregister function.
  registerNavGuard: (fn: () => void | Promise<void>) => () => void;
  runNavGuards: () => Promise<void>;
  // The review step registers a hook that runs when the user clicks Finish;
  // returning false cancels the exit (e.g. confirmation modal declined).
  registerBeforeFinish: (fn: () => boolean | Promise<boolean>) => () => void;
  runBeforeFinish: () => Promise<boolean>;

  refreshDocuments: (type: DocumentType) => Promise<void>;
  refreshRuleViolatingPolicies: () => Promise<void>;
  refreshLastSync: () => Promise<void>;
  refreshDemographics: () => Promise<void>;
  refreshPrograms: () => Promise<void>;
  refreshPoliciesWithoutProgram: () => Promise<void>;
}

const WIZARD_KEY: InjectionKey<DataReadinessWizardContext> = Symbol('DataReadinessWizard');

export function provideDataReadinessWizard(accountId: Ref<number>): DataReadinessWizardContext {
  const currentStep = ref(0);

  // For now every wizard step works with current, CAC-only policies, so these
  // stay at their defaults — the per-step filter toggles were removed from the
  // UI. The refs (and the query params fed from them) are kept as the single
  // filter source for when filtering is reintroduced.
  const showCurrentOnly = ref(true);
  const showNonCAC = ref(false);

  const binderRows = ref<PolicyDocumentStatusRow[]>([]);
  const policyRows = ref<PolicyDocumentStatusRow[]>([]);
  const ruleViolatingPolicies = ref<RuleViolatingPolicy[]>([]);
  const lastEpicSync = ref<EpicLastSync | null>(null);
  const demographicsInfo = ref<DemographicsInfo | null>(null);
  const programs = ref<Program[]>([]);
  const ruleViolatingPrograms = ref<RuleViolatingProgram[]>([]);
  const programSummaries = ref<ProgramSummary[]>([]);
  const policiesWithoutProgram = ref<EpicPolicy[]>([]);

  const loadingBinders = ref(false);
  const loadingPolicies = ref(false);
  const loadingRules = ref(false);
  const loadingLastSync = ref(false);
  const loadingDemographics = ref(false);
  const loadingPrograms = ref(false);
  const loadingProgramSummaries = ref(false);
  const loadingPoliciesWithoutProgram = ref(false);

  // Distinguish "loaded and empty" from "never loaded" so issue counts can
  // report unknown (null) instead of a false zero.
  const hasLoadedBinders = ref(false);
  const hasLoadedPolicies = ref(false);
  const hasLoadedRules = ref(false);
  const hasLoadedPrograms = ref(false);
  const hasLoadedPoliciesWithoutProgram = ref(false);

  const footerHint = ref<FooterHint | null>(null);

  const canAdvance = ref(true);
  const navGuards = new Set<() => void | Promise<void>>();
  let beforeFinishHook: (() => boolean | Promise<boolean>) | null = null;

  const metaData = ref<Record<string, unknown>>({});
  const isLoadingState = ref(false);
  const route = useRoute();

  function clampStep(n: number): number {
    if (n < 0) return 0;
    if (n >= WIZARD_STEPS.length) return WIZARD_STEPS.length - 1;
    return n;
  }

  // Furthest step the user has ever reached, so the stepper can keep showing
  // check/warning states for steps ahead of the current one after going back.
  // Persisted in metaData; treated as untrusted on read (clamped, non-numbers
  // ignored) and rewritten on every advance, so bad values self-heal.
  const maxStepReached = ref(0);

  function restoreMaxStepReached() {
    const raw = metaData.value['maxStepReached'];
    const stored = typeof raw === 'number' && Number.isInteger(raw) ? clampStep(raw) : 0;
    maxStepReached.value = Math.max(stored, currentStep.value);
    metaData.value = { ...metaData.value, maxStepReached: maxStepReached.value };
  }

  async function loadWizardState(): Promise<void> {
    isLoadingState.value = true;
    try {
      const server: WizardState = await wizardService.getState(accountId.value);
      metaData.value = server.metaData ?? {};

      const urlStepRaw = route.query.step;
      const urlStep =
        typeof urlStepRaw === 'string' ? Number.parseInt(urlStepRaw, 10) : NaN;

      if (!Number.isNaN(urlStep)) {
        // URL step is 1-indexed (see AccountDataReadinessWizard.vue); currentStep is 0-indexed.
        currentStep.value = clampStep(urlStep - 1);
        restoreMaxStepReached();
        // URL won — persist immediately (don't rely on the watcher, which is
        // gated by initialLoadDone and won't fire until the load finishes).
        void wizardService
          .putState(accountId.value, {
            currentStep: currentStep.value,
            metaData: metaData.value,
          })
          .catch(() => undefined);
      } else {
        currentStep.value = clampStep(server.currentStep);
        restoreMaxStepReached();
      }
    } catch {
      currentStep.value = 0;
      metaData.value = {};
      maxStepReached.value = 0;
      // Auto-saves would PUT our empty local state over whatever the server
      // has; with the load failed, preserving server data beats persisting
      // this session's progress.
      initialLoadFailed = true;
    } finally {
      isLoadingState.value = false;
      // Defer to the next tick so any pending watcher microtasks for the
      // server-side load itself (e.g. metaData reassignment) have flushed
      // before we open the gate. Without this, async-flush watchers can
      // observe the load and trigger a spurious PUT.
      await nextTick();
      initialLoadDone = true;
    }
  }

  onMounted(() => {
    void loadWizardState();
    // Load every step's data source up front so the stepper's per-step issue
    // indicators are accurate regardless of which step the user lands on.
    void Promise.allSettled([
      refreshDocuments('Binder'),
      refreshDocuments('Policy'),
      refreshRuleViolatingPolicies(),
      refreshDemographics(),
      refreshPrograms(),
      refreshPoliciesWithoutProgram(),
    ]);
  });

  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let initialLoadDone = false;
  let initialLoadFailed = false;

  function scheduleSave() {
    if (!initialLoadDone || initialLoadFailed) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void wizardService
        .putState(accountId.value, {
          currentStep: currentStep.value,
          metaData: metaData.value,
        })
        .catch(() => {
          // Surfacing left to caller / global error handler;
          // we deliberately don't revert currentStep.
        });
    }, 300);
  }

  function mergeMetaData(patch: Record<string, unknown>) {
    metaData.value = { ...metaData.value, ...patch };
  }

  watch([currentStep, metaData], () => {
    scheduleSave();
  }, { deep: true });

  watch(currentStep, (n) => {
    if (n > maxStepReached.value) {
      maxStepReached.value = n;
      mergeMetaData({ maxStepReached: n });
    }
  });

  onBeforeUnmount(() => {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
  });

  function setStep(n: number) {
    currentStep.value = n;
  }

  function setFooterHint(hint: FooterHint | null) {
    footerHint.value = hint;
  }

  function setCanAdvance(v: boolean) {
    canAdvance.value = v;
  }

  function registerNavGuard(fn: () => void | Promise<void>): () => void {
    navGuards.add(fn);
    return () => {
      navGuards.delete(fn);
    };
  }

  async function runNavGuards(): Promise<void> {
    for (const fn of navGuards) {
      await fn();
    }
  }

  function registerBeforeFinish(fn: () => boolean | Promise<boolean>): () => void {
    beforeFinishHook = fn;
    return () => {
      if (beforeFinishHook === fn) beforeFinishHook = null;
    };
  }

  async function runBeforeFinish(): Promise<boolean> {
    if (!beforeFinishHook) return true;
    return await beforeFinishHook();
  }

  async function refreshDocuments(type: DocumentType): Promise<void> {
    const target = type === 'Binder' ? binderRows : policyRows;
    const loading = type === 'Binder' ? loadingBinders : loadingPolicies;
    const hasLoaded = type === 'Binder' ? hasLoadedBinders : hasLoadedPolicies;
    loading.value = true;
    try {
      target.value = await clientReadinessService.getPolicyDocumentStatus(accountId.value, {
        documentType: type,
        showCurrentOnly: showCurrentOnly.value,
        showNonCAC: showNonCAC.value,
      });
      hasLoaded.value = true;
    } finally {
      loading.value = false;
    }
  }

  async function refreshRuleViolatingPolicies(): Promise<void> {
    loadingRules.value = true;
    try {
      ruleViolatingPolicies.value = await clientReadinessService.getRuleViolatingPolicies(accountId.value, {
        showCurrentOnly: showCurrentOnly.value,
        showNonCAC: showNonCAC.value,
      });
      hasLoadedRules.value = true;
    } finally {
      loadingRules.value = false;
    }
  }

  async function refreshDemographics(): Promise<void> {
    loadingDemographics.value = true;
    try {
      const account = await accountService.findById(accountId.value);
      demographicsInfo.value = {
        domainName: account.domainName || null,
        noDomain: account.noDomain,
      };
    } finally {
      loadingDemographics.value = false;
    }
  }

  async function refreshPrograms(): Promise<void> {
    loadingPrograms.value = true;
    loadingProgramSummaries.value = true;
    try {
      const [list, summaries, contacts, violators] = await Promise.all([
        accountService.getProgramsForAccount(accountId.value, !showCurrentOnly.value, false, true),
        accountService.getProgramSummary(accountId.value, false, true),
        accountService.getActiveContacts(accountId.value, true, false).catch(() => [] as AccountContact[]),
        clientReadinessService.getRuleViolatingPrograms(accountId.value, {
          showCurrentOnly: showCurrentOnly.value,
          showNonCAC: showNonCAC.value,
        }),
      ]);
      programs.value = list;
      // Attach a per-program ProgramContact assembled from active contacts so
      // AccountProgramContact can group them by servicing role for display.
      const contactsByProgramId = groupActiveContactsByProgram(contacts);
      programSummaries.value = summaries.map((s) => ({
        ...s,
        accountContact: contactsByProgramId.get(s.programId) ?? s.accountContact,
      }));
      ruleViolatingPrograms.value = violators;
      hasLoadedPrograms.value = true;
    } finally {
      loadingPrograms.value = false;
      loadingProgramSummaries.value = false;
    }
  }

  function groupActiveContactsByProgram(contacts: AccountContact[]): Map<number, ProgramContact> {
    const byProgram = new Map<number, AccountContact[]>();
    for (const c of contacts) {
      const list = byProgram.get(c.programID);
      if (list) list.push(c);
      else byProgram.set(c.programID, [c]);
    }
    const result = new Map<number, ProgramContact>();
    for (const [programID, list] of byProgram) {
      const rows: AccountContactRow[] = list.map((c) => {
        const row = new AccountContactRow();
        row.roles = [c.serviceTitle];
        row.contacts = [c];
        return row;
      });
      result.set(programID, { programID, contacts: rows });
    }
    return result;
  }

  async function refreshPoliciesWithoutProgram(): Promise<void> {
    loadingPoliciesWithoutProgram.value = true;
    try {
      policiesWithoutProgram.value = await accountService.policiesById(
        accountId.value,
        !showCurrentOnly.value,
        true,
        showNonCAC.value,
        false,
      );
      hasLoadedPoliciesWithoutProgram.value = true;
    } finally {
      loadingPoliciesWithoutProgram.value = false;
    }
  }

  const stepIssueCounts = computed<(number | null)[]>(() =>
    WIZARD_STEPS.map((step) => {
      switch (step.key) {
        case 'binders':
          return hasLoadedBinders.value ? countDocumentIssues(binderRows.value) : null;
        case 'policies':
          return hasLoadedPolicies.value ? countDocumentIssues(policyRows.value) : null;
        case 'epic':
          return hasLoadedRules.value ? countEpicIssues(ruleViolatingPolicies.value) : null;
        case 'demographics':
          return demographicsInfo.value ? countDemographicsIssues(demographicsInfo.value.domainName, demographicsInfo.value.noDomain) : null;
        case 'policyDetails':
          return hasLoadedRules.value ? countPolicyDetailIssues(ruleViolatingPolicies.value) : null;
        case 'programGrouping': {
          if (!hasLoadedPrograms.value || !hasLoadedPoliciesWithoutProgram.value) return null;
          const missingRole = ruleViolatingPrograms.value.filter((p) => p.noServicingRolesRule).length;
          return missingRole + policiesWithoutProgram.value.length;
        }
        default:
          return null;
      }
    }),
  );

  async function refreshLastSync(): Promise<void> {
    loadingLastSync.value = true;
    try {
      lastEpicSync.value = await epicService.getLastSync();
    } finally {
      loadingLastSync.value = false;
    }
  }

  const ctx: DataReadinessWizardContext = {
    accountId,
    currentStep,
    setStep,
    maxStepReached,
    showCurrentOnly,
    showNonCAC,
    binderRows,
    policyRows,
    ruleViolatingPolicies,
    lastEpicSync,
    demographicsInfo,
    programs,
    ruleViolatingPrograms,
    programSummaries,
    policiesWithoutProgram,
    loadingBinders,
    loadingPolicies,
    loadingRules,
    loadingLastSync,
    loadingDemographics,
    loadingPrograms,
    loadingProgramSummaries,
    loadingPoliciesWithoutProgram,
    stepIssueCounts,
    footerHint,
    metaData,
    isLoadingState,
    loadWizardState,
    mergeMetaData,
    setFooterHint,
    canAdvance,
    setCanAdvance,
    registerNavGuard,
    runNavGuards,
    registerBeforeFinish,
    runBeforeFinish,
    refreshDocuments,
    refreshRuleViolatingPolicies,
    refreshLastSync,
    refreshDemographics,
    refreshPrograms,
    refreshPoliciesWithoutProgram,
  };

  provide(WIZARD_KEY, ctx);
  return ctx;
}

export function useDataReadinessWizard(): DataReadinessWizardContext {
  const ctx = inject(WIZARD_KEY);
  if (!ctx) {
    throw new Error('useDataReadinessWizard must be called within a provideDataReadinessWizard scope');
  }
  return ctx;
}

// label = table rows / general reference; shortLabel = stepper circles.
export const WIZARD_STEPS = [
  { key: 'binders', label: 'Binder docs' },
  { key: 'policies', label: 'Policy docs' },
  { key: 'epic', label: 'Epic cleanup' },
  { key: 'demographics', label: 'Client demographics', shortLabel: 'Demographics' },
  { key: 'policyDetails', label: 'Policy details' },
  { key: 'programGrouping', label: 'Program grouping', shortLabel: 'Programs' },
  { key: 'review', label: 'Review & finalize', shortLabel: 'Review' },
] as const;

export const totalWizardSteps = computed(() => WIZARD_STEPS.length);
